/**
 * Outstanding Invoices Table Component
 * Phase 6 Batch 3 - Core 4 Reports
 *
 * Table showing unpaid invoices with aging and client details
 */
import { useOutstandingInvoices } from '@/hooks/useDashboardReports';
import { AlertCircle } from 'lucide-react';
import { useState } from 'react';
import { parseLocalDateString } from '@/lib/localDate';

export function OutstandingInvoicesTable() {
  const { data, isLoading, error } = useOutstandingInvoices();
  const [sortBy, setSortBy] = useState<'due_date' | 'amount' | 'days'>('due_date');

  if (isLoading) {
    return (
      <div className="bg-white p-6 rounded-lg shadow">
        <div className="h-4 bg-gray-200 rounded w-1/3 mb-4 animate-pulse"></div>
        <div className="space-y-2">
          {[...Array(5)].map((_, i) => (
            <div key={i} className="h-16 bg-gray-100 rounded animate-pulse"></div>
          ))}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700">
        <AlertCircle className="inline mr-2" size={20} />
        Failed to load outstanding invoices
      </div>
    );
  }

  const invoices = data?.data || [];
  const summary = data?.summary || {
    total_outstanding: 0,
    total_overdue: 0,
    count: 0,
    overdue_count: 0,
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
    }).format(amount);
  };

  const formatDate = (dateStr: string) => {
    // due_date is a date-only string. Parsing with the bare Date
    // constructor reads it as UTC midnight, which displays as the previous
    // calendar day west of UTC.
    const d = parseLocalDateString(dateStr);
    if (!d) return '—';
    return d.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  // Sort invoices
  const sortedInvoices = [...invoices].sort((a, b) => {
    if (sortBy === 'due_date') {
      return new Date(a.due_date).getTime() - new Date(b.due_date).getTime();
    } else if (sortBy === 'amount') {
      return b.total_amount - a.total_amount;
    } else {
      return (b.days_overdue || 0) - (a.days_overdue || 0);
    }
  });

  return (
    <div className="bg-white p-6 rounded-lg shadow" data-testid="outstanding-invoices-table">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-semibold text-gray-900">
          Outstanding Invoices
        </h2>
        <div className="flex gap-2 text-sm">
          <button
            onClick={() => setSortBy('due_date')}
            className={`px-3 py-1 rounded ${sortBy === 'due_date' ? 'bg-blue-600 text-white' : 'bg-gray-200'}`}
          >
            Due Date
          </button>
          <button
            onClick={() => setSortBy('amount')}
            className={`px-3 py-1 rounded ${sortBy === 'amount' ? 'bg-blue-600 text-white' : 'bg-gray-200'}`}
          >
            Amount
          </button>
          <button
            onClick={() => setSortBy('days')}
            className={`px-3 py-1 rounded ${sortBy === 'days' ? 'bg-blue-600 text-white' : 'bg-gray-200'}`}
          >
            Days Overdue
          </button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-4 gap-4 mb-6">
        <div className="bg-blue-50 p-4 rounded-lg">
          <div className="text-sm text-gray-600">Total Outstanding</div>
          <div className="text-xl font-bold text-sky-600">{formatCurrency(summary.total_outstanding)}</div>
        </div>
        <div className="bg-red-50 p-4 rounded-lg">
          <div className="text-sm text-gray-600">Total Overdue</div>
          <div className="text-xl font-bold text-red-600">{formatCurrency(summary.total_overdue)}</div>
        </div>
        <div className="bg-gray-50 p-4 rounded-lg">
          <div className="text-sm text-gray-600">Invoice Count</div>
          <div className="text-xl font-bold text-gray-900">{summary.count}</div>
        </div>
        <div className="bg-orange-50 p-4 rounded-lg">
          <div className="text-sm text-gray-600">Overdue Count</div>
          <div className="text-xl font-bold text-orange-600">{summary.overdue_count}</div>
        </div>
      </div>

      {/* Table */}
      {sortedInvoices.length > 0 ? (
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Invoice #</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Project</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Client</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Amount</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Due Date</th>
                <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Status</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {sortedInvoices.map((invoice) => (
                <tr key={invoice.invoice_id} className="hover:bg-gray-50">
                  <td className="px-4 py-3 text-sm font-medium text-gray-900">{invoice.invoice_number}</td>
                  <td className="px-4 py-3 text-sm text-gray-600">
                    {/* QF-01: Show "Proposal" instead of "#null" for proposals without project_number */}
                    {invoice.project_number ? `#${invoice.project_number}` : 'Proposal'} - {invoice.project_name}
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-600">{invoice.client_name}</td>
                  <td className="px-4 py-3 text-sm font-semibold text-right">{formatCurrency(invoice.total_amount)}</td>
                  <td className="px-4 py-3 text-sm text-gray-600">{formatDate(invoice.due_date)}</td>
                  <td className="px-4 py-3 text-center">
                    {invoice.status === 'overdue' ? (
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                        {invoice.days_overdue} days overdue
                      </span>
                    ) : (
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                        Current ({invoice.days_outstanding} days)
                      </span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="text-center py-12 text-gray-500">
          No outstanding invoices
        </div>
      )}
    </div>
  );
}
